/**
 * Report photos — vocabulary, grouping and the lazy image loader.
 *
 * Backend contract: `budgets-draws-report-photos.md` (live 2026-09-21) and
 * `app/models/report_photos.py`. `GET /{prefix}/report_photo_reviews/{dealId}`
 * returns every review on the deal, newest report first; each photo carries an
 * `imagePath` the read route derives from its stored `blobPath`, or `null` when
 * the image was catalogued but never stored (a logo, a cover page).
 *
 * Grouping rules from the doc:
 *   • group the page by `reportKind` (feasibility vs inspection)
 *   • several inspection files for ONE draw (revisions) come back as separate
 *     reviews sharing a `drawNumber` — group them under the draw. Which file
 *     governs is decided by the servicing build, not here.
 *   • a photo links to budget rows by `lineItemIds` (ScopeOfWork.lineItems[].oid),
 *     NEVER by the printed `budgetLines` names.
 */
import { useEffect, useRef, useState } from "react";

// Literal values from app/models/report_photos.py:29.
export const PHOTO_CATEGORY_LABEL = {
  site_exterior: "Exterior",
  site_interior: "Interior",
  progress_work: "Progress",
  materials_on_site: "Materials",
  plans_drawings: "Plans",
  map_or_aerial: "Map / aerial",
  document_page: "Document page",
  other: "Other",
};

/** "Default view hides `document_page` and `other`." */
export const DEFAULT_HIDDEN_CATEGORIES = ["document_page", "other"];

export const PHOTO_STAGE_LABEL = {
  not_started: "Not started",
  in_progress: "In progress",
  complete: "Complete",
  as_is: "As-is",
  "n/a": "N/A",
};

/** Stage badge tones — progression, not severity. */
export const PHOTO_STAGE_TONE = {
  complete: "good",
  in_progress: "info",
  not_started: "neutral",
  as_is: "neutral",
  "n/a": "neutral",
};

/** A photo with no stored image is catalogued only — never render a thumbnail. */
export const hasImage = (photo) => Boolean(photo?.imagePath);

export const photoKey = (review, photo) =>
  `${review?._id || review?.oid || review?.sourceBlobName || "r"}:${photo?.index}`;

/**
 * Reviews split by kind, with inspections gathered under their draw.
 *
 * @returns {{ feasibility: object[], inspectionDraws: {drawNumber, reviews}[] }}
 */
export const groupReviews = (reviews) => {
  const feasibility = [];
  const byDraw = new Map();
  (reviews || []).forEach((r) => {
    if (r?.reportKind === "feasibility") {
      feasibility.push(r);
      return;
    }
    // drawNumber is inspections only; an unnumbered one gets its own bucket
    // rather than colliding with every other unnumbered file.
    const key = r?.drawNumber ?? `unnumbered:${r?._id || r?.sourceBlobName}`;
    if (!byDraw.has(key)) {
      byDraw.set(key, { drawNumber: r?.drawNumber ?? null, reviews: [] });
    }
    byDraw.get(key).reviews.push(r);
  });
  const inspectionDraws = [...byDraw.values()].sort(
    (a, b) => (b.drawNumber ?? -1) - (a.drawNumber ?? -1),
  );
  return { feasibility, inspectionDraws };
};

/**
 * Every photo that evidences a given ScopeOfWork line, across ALL reports,
 * OLDEST FIRST — the doc's requirement, so the budget grid's camera icon shows
 * the line progressing over the life of the loan.
 *
 * @returns {Map<string, {review, photo}[]>} keyed by lineItemId
 */
export const photosByLineItem = (reviews) => {
  const map = new Map();
  // The read route sorts newest first; walk it backwards for oldest first.
  [...(reviews || [])].reverse().forEach((review) => {
    (review?.photos || []).forEach((photo) => {
      (photo?.lineItemIds || []).forEach((id) => {
        const key = String(id);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push({ review, photo });
      });
    });
  });
  return map;
};

/** Photos in report page order — the strip's order per the doc. */
export const orderedPhotos = (review) =>
  [...(review?.photos || [])].sort(
    (a, b) => (a?.page ?? 0) - (b?.page ?? 0) || (a?.index ?? 0) - (b?.index ?? 0),
  );

/**
 * Lazily resolve one photo's bytes to an object URL, once, and revoke it on
 * unmount. The image endpoint needs the Bearer header, so an <img src> pointed
 * straight at it would 401 — every thumbnail goes through here.
 *
 * `enabled` is what makes it LAZY: the strip passes false until the thumbnail
 * has been near the viewport, so a 40-photo report doesn't fire 40 requests.
 */
export const useReportPhotoUrl = (api, dealId, imagePath, enabled = true) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const urlRef = useRef(null);

  useEffect(() => {
    if (!enabled || !dealId || !imagePath) return undefined;
    let alive = true;
    api
      .getReportPhotoUrl(dealId, imagePath)
      .then((objectUrl) => {
        if (!alive) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        urlRef.current = objectUrl;
        setUrl(objectUrl);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [api, dealId, imagePath, enabled]);

  // Revoke on unmount / when the path changes — an object URL held past its
  // element leaks the decoded image for the life of the document.
  useEffect(
    () => () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    },
    [imagePath],
  );

  return { url, failed };
};

/**
 * True once the element has been within `rootMargin` of the viewport. One
 * observer per thumbnail, disconnected on first hit — the strip scrolls
 * horizontally inside a scrolling tab, and IntersectionObserver handles both
 * axes without a scroll listener.
 */
export const useNearViewport = (ref, rootMargin = "300px") => {
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || near) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setNear(true); // no observer (jsdom / very old browser): just load
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, near, rootMargin]);
  return near;
};
